<script setup lang="ts">
  import { storeToRefs } from 'pinia';
  import { computed, ref } from 'vue';

  import TaskEmailResponse from '@/components/tasks/TaskEmailResponse.vue';
  import TaskViewContainer from '@/components/tasks/TaskViewContainer.vue';
  import { useTaskCollectionStore } from '@/store';

  const emit = defineEmits<{
    (e: 'openDismissAlert'): void;
    (e: 'close'): void;
    (e: 'openConfirmComplete'): void;
    (e: 'enableCloseOnRouteChange'): void;
    (e: 'forceTaskTableRefresh'): void;
  }>();
  const ManualCallTaskRef = ref();
  const TaskEmailResponseRef = ref();
  const DisableContactTaskRef = ref();

  const taskCollectionStore = useTaskCollectionStore();
  const { currentTask } = storeToRefs(taskCollectionStore);
  const { navigationDirection } = storeToRefs(taskCollectionStore);

  const transitionName = computed(() => {
    return (navigationDirection.value ?? 'forward') === 'backward'
      ? 'task-transition-backward'
      : 'task-transition-forward';
  });

  defineExpose({
    DisableContactTaskRef,
    ManualCallTaskRef,
    TaskEmailResponseRef,
  });
</script>

<template>
  <Transition :name="transitionName" mode="out-in" appear>
    <TaskViewContainer
      v-if="
        currentTask?.taskComponentType && ['manual_call'].includes(currentTask?.taskComponentType)
      "
      :key="`${currentTask?.id}-view-container`"
      :task="currentTask"
      @enable-close-on-route-change="emit('enableCloseOnRouteChange')"
      @open-confirm-complete="emit('openConfirmComplete')"
      @force-task-table-refresh="emit('forceTaskTableRefresh')"
      @open-dismiss-alert="emit('openDismissAlert')"
      @close="emit('close')"
    />

    <TaskEmailResponse
      v-else-if="currentTask?.taskComponentType === 'email_response'"
      :key="`${currentTask?.id}-email-response`"
      ref="TaskEmailResponseRef"
      @open-dismiss-alert="emit('openDismissAlert')"
      @close="emit('close')"
    />

    <TaskEmailResponse
      v-else
      :key="`${currentTask?.id}-fallback`"
      ref="TaskEmailResponseRef"
      @open-dismiss-alert="emit('openDismissAlert')"
      @close="emit('close')"
    />
  </Transition>
</template>

<style scoped>
  /* Forward transition animations (next task) */
  .task-transition-forward-enter-active {
    transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }

  .task-transition-forward-leave-active {
    transition: all 0.3s cubic-bezier(0.55, 0.085, 0.68, 0.53);
  }

  .task-transition-forward-enter-from {
    opacity: 0;
    transform: translateX(100%) scale(0.95);
  }

  .task-transition-forward-leave-to {
    opacity: 0;
    transform: translateX(-100%) scale(1.02);
  }

  .task-transition-forward-enter-to,
  .task-transition-forward-leave-from {
    opacity: 1;
    transform: translateX(0) translateY(0) scale(1);
  }

  /* Backward transition animations (previous task) */
  .task-transition-backward-enter-active {
    transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }

  .task-transition-backward-leave-active {
    transition: all 0.3s cubic-bezier(0.55, 0.085, 0.68, 0.53);
  }

  .task-transition-backward-enter-from {
    opacity: 0;
    transform: translateX(-100%) scale(0.95);
  }

  .task-transition-backward-leave-to {
    opacity: 0;
    transform: translateX(100%) scale(1.02);
  }

  .task-transition-backward-enter-to,
  .task-transition-backward-leave-from {
    opacity: 1;
    transform: translateX(0) translateY(0) scale(1);
  }
</style>
